import Box from "@/common/components/Box";
import ResumeItem from "@/common/components/ResumeItem";
import { useForwardingTabContext } from "@/features/maintenance/features/workflows/hooks/forwardings/useForwardingTabContext";
import TransportAutoEntriesComplete from "./Transport/AutoEntries/Complete";
import { formatDateTime } from "@/common/utils/formatters";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { checkinEntryOptions } from "@/features/maintenance/features/workflows/constants/forwardings/steps/checkinEntry/checkinEntryOptions.constant";
import { cn } from "@/common/utils/cn";
import {
  MaintenanceForwardingSectionStyle,
  maintenanceForwardingSectionStyles,
} from "@/features/maintenance/features/workflows/constants/forwardings/maintenanceForwardingSectionStyles.constant";
import { MaintenanceForwardingStatus } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";
import { useMemo } from "react";

interface MaintenanceWorkflowForwardingCheckinEntrySectionProps {
  showTransportAutoEntriesDeliveryCodeWithComplete?: boolean;
}

const MaintenanceWorkflowForwardingCheckinEntrySection = ({
  showTransportAutoEntriesDeliveryCodeWithComplete,
}: MaintenanceWorkflowForwardingCheckinEntrySectionProps) => {
  const { forwarding, forwardingUpdatedAt } = useForwardingTabContext();

  const sectionStyle = useMemo(() => {
    if (forwarding?.checkin_entry?.completed_by) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Completed];
    }

    if (forwarding?.status === MaintenanceForwardingStatus.OnGoingCheckin) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Pending];
    }

    return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Default];
  }, [forwardingUpdatedAt]);

  return (
    <Box className={cn('p-4 flex flex-col gap-4', sectionStyle)}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">
            Método de check-in
          </p>

          {forwarding?.checkin_entry?.completed_by && (
            <div className="flex items-center gap-1 -mb-1">
              <p className="text-valid text-xs">
                Check-in concluído
              </p>
            </div>
          )}
        </div>

        {showTransportAutoEntriesDeliveryCodeWithComplete && (
          <TransportAutoEntriesComplete
            checkinEntry={forwarding.checkin_entry}
          />
        )}
      </div>

      <div className="flex gap-4">
        {forwarding?.checkin_entry?.photos && forwarding?.checkin_entry?.photos?.length > 0 && (
          <ImageCarousel
            imageClassName='object-contain'
            className='size-52'
            files={forwarding?.checkin_entry?.photos?.map((file) => ({ id: file.id, file: file.url }))}
            showRemoveButton={false}
            showExpandButton
          />
        )}
        <div className="flex flex-col gap-3">
          <div className="flex gap-4">
            {forwarding?.checkin_entry?.completed_at && (
              <ResumeItem
                label="Check-in realizado"
                value={formatDateTime(new Date(forwarding?.checkin_entry?.completed_at))}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}

            {forwarding?.checkin_entry?.completed_at && forwarding?.checkin_entry?.completed_by && (
              <ResumeItem
                label="Usuário que realizou o check-in"
                value={forwarding.checkin_entry.completed_by.full_name}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>

          <div className="flex gap-4">
            {forwarding?.checkin_entry_type && (
              <ResumeItem
                label="Tipo de Transporte"
                value={checkinEntryOptions[forwarding.checkin_entry_type].title || '--'}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}

            {forwarding?.checkin_entry?.responsible && !forwarding?.checkin_entry?.completed_by && (
              <ResumeItem
                label="Responsável por Transporte"
                value={forwarding?.checkin_entry?.responsible.full_name || '--'}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>

          {forwarding?.checkin_entry?.observations && (
            <ResumeItem
              label='Observações de check-in'
              value={forwarding?.checkin_entry.observations || '--'}
              valueClassName='text-sm'
            />
          )}
        </div>
      </div>
    </Box>
  )
}

export default MaintenanceWorkflowForwardingCheckinEntrySection;
